import type { Doc } from "@/lib/types";

type ProjectFocusDoc = Pick<Doc, "id" | "projectId" | "pinned" | "updatedAt">;

/**
 * Keep the current document when it already belongs to the selected project;
 * otherwise open the first document the project view will show.
 */
export function getProjectLensSelection(
  docs: readonly ProjectFocusDoc[],
  projectId: string,
  selectedDocId: string | null
): string | null {
  const selected = selectedDocId
    ? docs.find((doc) => doc.id === selectedDocId)
    : null;

  if (selected?.projectId === projectId) return selected.id;

  const firstProjectDoc = docs
    .filter((doc) => doc.projectId === projectId)
    .sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return b.updatedAt - a.updatedAt;
    })[0];

  return firstProjectDoc?.id ?? null;
}

function parseStoredIds(raw: string | null): Set<string> {
  if (!raw) return new Set();

  try {
    const value: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(value)
        ? value.filter((item): item is string => typeof item === "string")
        : []
    );
  } catch {
    return new Set();
  }
}

/**
 * Collapse each low-prominence node exactly once, when this browser first
 * encounters it. A separate seen set preserves an explicit later expansion.
 */
export function restoreLowProminenceCollapseState(
  collapsedRaw: string | null,
  seenRaw: string | null,
  lowProminenceIds: readonly string[]
): { collapsed: Set<string>; seen: Set<string> } {
  const collapsed = parseStoredIds(collapsedRaw);
  const seen = parseStoredIds(seenRaw);

  for (const id of lowProminenceIds) {
    if (!seen.has(id)) collapsed.add(id);
    seen.add(id);
  }

  return { collapsed, seen };
}
